'use client'; // Add this if using App Router

import { useState, FormEvent } from 'react';
import { Mail } from 'lucide-react';

type SubmissionStatus = 'idle' | 'loading' | 'success' | 'error';

interface MailerLiteError {
  message: string;
  errors?: Record<string, string[]>;
}

interface ApiResponse {
  message: string;
  error?: string | MailerLiteError;
}

const NewsletterSection: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [status, setStatus] = useState<SubmissionStatus>('idle');
  const [message, setMessage] = useState<string>('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    // Sanitize email
    const sanitizedEmail = email.trim(); // Remove leading/trailing whitespace

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!sanitizedEmail || !emailRegex.test(sanitizedEmail)) {
      setStatus('error');
      setMessage('Please enter a valid email address');
      return;
    }

    setStatus('loading');
    setMessage('');

    try {
      const response = await fetch('/api/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      let data: ApiResponse | null = null;

      try {
        // Attempt to parse the response as JSON
        data = await response.json();
      } catch {
        // Handle cases where the response body is empty or invalid
        data = { message: 'Unexpected response from the server' };
      }

      if (response.ok) {
        setStatus('success');
        setMessage(data?.message || 'Thanks for subscribing! Check your email for confirmation.');
        setEmail('');
      } else {
        setStatus('error');
        setMessage(data?.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Network error. Please try again.');
      console.error('Newsletter subscription error:', error);
    }
  };

  const isLoading = status === 'loading';
  const isDisabled = isLoading || !email.trim();

  return (
    <section className="px-4 py-20 mx-auto max-w-7xl sm:px-6 lg:px-8">
      <div className="p-8 text-center text-white bg-gradient-to-r from-pink-500 via-purple-600 to-blue-600 rounded-3xl lg:p-12">
        <Mail className="w-16 h-16 mx-auto mb-6 opacity-90" />
        <h2 className="mb-4 text-4xl font-bold">Stay Updated</h2>
        <p className="max-w-2xl mx-auto mb-8 text-xl opacity-90">
          Subscribe to our newsletter for updates, free coloring pages, and
          exclusive offers!
        </p>
        
        <form 
          onSubmit={handleSubmit} 
          className="flex flex-col items-center justify-center max-w-md gap-4 mx-auto sm:flex-row"
        >
          <input
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
            className="w-full px-6 py-4 text-gray-900 border-0 rounded-full shadow-lg focus:ring-4 focus:ring-white/30 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            required
          />
          <button 
            type="submit"
            disabled={isDisabled}
            className="px-8 py-4 font-semibold text-purple-600 transition-all duration-300 transform bg-white rounded-full shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap disabled:opacity-50 disabled:hover:scale-100 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Subscribing...' : 'Subscribe Now'}
          </button>
        </form>

        {/* Status Messages */}
        {message && (
          <div 
            className={`mt-4 p-3 rounded-full transition-all duration-300 ${
              status === 'success' 
                ? 'bg-green-500/20 text-green-100' 
                : 'bg-red-500/20 text-red-100'
            }`}
            role="alert"
            aria-live="polite"
          >
            {message}
          </div>
        )}
      </div>
    </section>
  );
};

export default NewsletterSection;